
import { Component, Prop, Vue } from 'vue-property-decorator';
import { StoreModel } from '@/models/store';
import HeaderTitle from '../components/HeaderTitle.vue';
import ActionButton from '@/components/ActionButton.vue';
import TryNewAtlasBanner from '@/components/TryNewAtlasBanner.vue';
import {
  getFeatureVersion,
  setFeatureVersion,
  importingStatusSwitchTarget,
} from '@/config/atlasFeatures';
import { EventBus } from '@/main';
import { FunnelEventModel } from '@/models/funnelEvent';
import { productSyncJobs } from '@/collections/product_sync_jobs';
import { ProductSyncJobModel } from '@/models/product_sync_job';
import { forceSyncResourceKey } from '@/utils/etlForceSyncResource';
import {
  hasCollectionsGraphqlPeriod,
  LEGACY_COLLECTION_RESOURCES,
} from '@/utils/collectionsResourceCount';

const toCamelCase = (text: string) => {
  const result = text.toLowerCase().replace(
    /([-_][a-z])/g,
    (group) =>
    group
      .toUpperCase()
      .replace('-', ' ')
      .replace('_', ' '),
  );
  return result.substring(0, 1).toUpperCase() + result.substring(1);
};

const datetimeToString = (datestring: Date) => {
    const dd = String(datestring.getDate()).padStart(2, '0');
    const mm = String(datestring.getMonth() + 1).padStart(2, '0'); // January is 0!
    const yyyy = datestring.getFullYear();
    const H = String(datestring.getHours()).padStart(2, '0');
    const M = String(datestring.getMinutes()).padStart(2, '0');
    const S = String(datestring.getSeconds()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd} ${H}:${M}:${S}`;
};

@Component({
  components: {
    HeaderTitle,
    ActionButton,
    TryNewAtlasBanner,
  },
})
export default class EtlStatusPage extends Vue {
  @Prop({ default: () => new StoreModel() })
  public store!: StoreModel;
  @Prop({ default: () => [] })
  public timeRangeLimit!: string[];
  @Prop({ default: false })
  public isAthenaFinished!: boolean;
  @Prop({default: null})
  public app!: any;
  public headers: any[] = [];
  public items: any[] = [];
  public sortBy: string = 'interval';
  public sortDesc: boolean = true;
  public loading: boolean = false;
  public triggerDataDialog: boolean = false;
  public syncResource: string = 'orders';
  public resourceUpToDate: boolean = false;
  public dataUptodateDialog: boolean = false;
  public loadingImportCount: boolean = false;

  // Clear Deleted Products feature
  public clearDeletedDialog: boolean = false;
  public activeJob: ProductSyncJobModel | null = null;
  public syncHistory: ProductSyncJobModel[] = [];
  public initiatingClear: boolean = false;
  public cancellingJob: boolean = false;
  public jobPollingInterval: number | null = null;
  public isPolling: boolean = false;
  public historyHeaders: any[] = [
    { text: 'Date', value: 'initiatedAt' },
    { text: 'Status', value: 'status' },
    { text: 'Duration', value: 'duration' },
    { text: 'Products Synced', value: 'totalProductsSynced' },
  ];

  /**
   * AS-10844. Both classic routes onto this component (/importing-status and
   * /status-page) are governed by the importingStatus flag; while it reads
   * `classic` the merchant is here rather than on atlas, so offer the switch.
   * Once it reads `atlas` the router guard redirects before this view renders,
   * so the banner would only ever flash — hide it.
   */
  public get showTryNewImportingStatusBanner(): boolean {
    return getFeatureVersion('importingStatus') === 'classic';
  }

  public switchImportingStatusToAtlas(): void {
    setFeatureVersion('importingStatus', 'atlas');
    // Single-sourced from ATLAS_FEATURE_LINKS so the forward switch and the
    // reverse redirect cannot drift apart.
    this.$router.push(importingStatusSwitchTarget());
  }

  public get isJobActive(): boolean {
    return this.activeJob !== null && this.activeJob.isActive();
  }

  public get canCancelJob(): boolean {
    return this.activeJob !== null && this.activeJob.canCancel();
  }

  public async created() {
    this.headers = [
      {
        text: 'Resource',
        value: 'resource',
      },
      {
        text: 'Schedule',
        value: 'interval',
      },
      {
        text: 'Last Importing',
        value: 'lastUpdatedAt',
      },
      {
        text: 'Next Time Importing',
        value: 'nextTimeEtl',
      },
      {
        text: 'Imported',
        value: 'count',
      },
      {
        text: 'Status',
        value: 'isUpToDate',
      },
      {
        text: 'Sync Data',
        value: 'actions',
      },
    ];

    this.loading = true;
    await this.store.getStoreData();
    this.renderETLData();
    try {
      const funnelEvent = new FunnelEventModel({
        name: 'VIEWED_STATUS_PAGE',
      });
      funnelEvent.create();
    } catch (error) {
      // skipped
    }
    this.loading = false;
    await this.loadETLImportedCount();

    // Load clear deleted products state
    await this.loadActiveJob();
    await this.loadSyncHistory();
  }

  public beforeDestroy() {
    this.stopJobPolling();
  }

  public renderETLData() {
    const dailyResources = [
      'locations',
      //'tender_transactions',
      'customers',
      'smart_collections',
      'custom_collections',
      'collections',
      //'collects',
      'product_metafields',
      //'payouts',
      'transactions',
      //'disputes',
      //'variant_metafields',
      //'suppliers',
      //'purchase_orders',
      //'stock_transfers',
      //'mimoran_purchase_orders',
    ];

    const nextTimeEtlMessage = 'Next time when you access Assisty!';
    // A store switched to the GraphQL `collections` extract keeps its last
    // custom_/smart_collections periods in the DB, which would otherwise sit at
    // "out of date" forever — the backend stopped creating new ones.
    const collectionsSwitched = hasCollectionsGraphqlPeriod(this.store.etlStatuses.items);
    this.items = [];
    const items: any = {};
    for (const item of this.store.etlStatuses.items) {

      // Skip ETL status rows that are inactive — they should not be displayed.
      if (item.status === 'INACTIVE') {
        continue;
      }

      if (collectionsSwitched && LEGACY_COLLECTION_RESOURCES.includes(item.resource)) {
        continue;
      }

      if (item.resource === 'orders' && item.etlType === 'etl_data_period') {
        if (!Object.keys(items).includes('orders')) {
          items.orders = {
            resource: 'Orders',
          };
        }
        if (this.store.currentPackage.price > 0) {
          items[item.resource].nextTimeEtl = this.getNextTimeETL(item, 1)
        } else {
          items[item.resource].nextTimeEtl = nextTimeEtlMessage;
        }
        if (this.store.currentPackage.price > 0) {
          items[item.resource].interval = 'Hourly';
        } else {
          items[item.resource].interval = 'Paid plan only';
        }
        items.orders['count'] = `${this.store.orderCount.modelCount} / ${this.store.orderCount.apiCount}`;
        items.orders.limitStartTime = item.limitStartTime;
        items.orders.startTime = item.startTime;
      }

      if (item.resource === 'sales_order_variant_olap'
          || item.resource === 'order_daily_sales_olap') {
        if (!Object.keys(items).includes('orders')) {
          items.orders = {
            resource: 'Orders',
          };
        }
        if (item.endTime
            && (!items.orders.endTime || new Date(item.endTime) > new Date(items.orders.endTime))
            ) {
          items.orders.endTime = datetimeToString(
            new Date(item.endTime),
          );
        }
      }

      if (item.resource === 'products' && item.etlType === 'etl_data_period') {
        if (!Object.keys(items).includes('products')) {
          items.products = {
            resource: 'Products',
          };
        }
        if (item.endTime) {
          const endTime = new Date(item.endTime);
          const currentendTime = new Date(items.products.endTime);
          if (!items.products.endTime
            || endTime.getTime() > currentendTime.getTime()
          ) {
              items.products.endTime = datetimeToString(endTime);
          }
        }
        if (this.store.currentPackage.price > 0) {
          items[item.resource].nextTimeEtl = this.getNextTimeETL(item, 1);
        } else {
          items[item.resource].nextTimeEtl = nextTimeEtlMessage;
        }
        if (this.store.currentPackage.price > 0) {
          items[item.resource].interval = 'Hourly';
        } else {
          items[item.resource].interval = 'Paid plan only';
        }

        items.products['count'] = `${this.store.productCount.modelCount} / ${this.store.productCount.apiCount}`;
        items.products.limitStartTime = item.limitStartTime;
        items.products.startTime = item.startTime;        
      }

      if (item.resource === 'products' && item.etlType === 'transform_request') {
        if (!Object.keys(items).includes('products')) {
          items.products = {
            resource: 'Products',
          };
        }
        if (item.endTime) {
          const endTime = new Date(item.endTime);
          const currentendTime = new Date(items.products.endTime);
          if (!items.products.endTime
            || endTime.getTime() > currentendTime.getTime()
          ) {
              items.products.endTime = datetimeToString(endTime);
          }
        }
        if (this.store.currentPackage.price > 0) {
          items[item.resource].interval = 'Hourly';
        } else {
          items[item.resource].interval = 'Paid plan only';
        }
      }

      /** inventory on hand */
      let inventoryOnHandResource = 'inventory_levels'
      if (item.resource === inventoryOnHandResource && item.etlType === 'etl_data_period') {
        if (!Object.keys(items).includes('inventory_on_hand')) {
          items.inventory_on_hand = {
            resource: 'Inventory On Hand',
          };
        }
        if (item.endTime) {
          const endTime = new Date(item.endTime);
          const currentendTime = new Date(items.inventory_on_hand.endTime);
          if (!items.inventory_on_hand.endTime
            || endTime.getTime() > currentendTime.getTime()
          ) {
              items.inventory_on_hand.endTime = datetimeToString(endTime);
          }
        }
        if (this.store.currentPackage.price > 0) {
          items.inventory_on_hand.nextTimeEtl = this.getNextTimeETL(item, 1);
        } else {
          items.inventory_on_hand.nextTimeEtl = nextTimeEtlMessage;
        }
        if (this.store.currentPackage.price > 0) {
          items.inventory_on_hand.interval = 'Hourly';
        } else {
          items.inventory_on_hand.interval = 'Paid plan only';
        }

        items.inventory_on_hand['count'] = `${this.store.inventoryItemCount.modelCount} / ${this.store.inventoryItemCount.apiCount}`;
        items.inventory_on_hand.limitStartTime = item.limitStartTime;
        items.inventory_on_hand.startTime = item.startTime;
        items.inventory_on_hand.lastUpdatedAt = item.lastUpdatedAt;    
      }

      if (this.store.currentPackage.price > 0) {
        inventoryOnHandResource = 'olap_current_inventory_levels'     
      }
      if (item.resource === inventoryOnHandResource && item.etlType === 'transform_request' ) {
        if (!Object.keys(items).includes('inventory_on_hand')) {
          items.inventory_on_hand = {
            resource: 'Inventory On Hand',
          };
        } else {
          items.inventory_on_hand.endTime = null;
        }
        if (item.endTime) {
          const endTime = new Date(item.endTime);
          const currentendTime = new Date(items.inventory_on_hand.endTime);
          if (!items.inventory_on_hand.endTime
            || endTime.getTime() > currentendTime.getTime()
          ) {
              items.inventory_on_hand.endTime = datetimeToString(endTime);
          }
        }
        if (this.store.currentPackage.price > 0) {
          items.inventory_on_hand.interval = 'Hourly';
        } else {
          items.inventory_on_hand.interval = 'Paid plan only';
        }
        if (item.lastUpdatedAt) {
          items.inventory_on_hand.lastUpdatedAt = item.lastUpdatedAt;
        }
      }

      /** incoming inventory */
      if (item.resource === 'inventory_level_quantities' && item.etlType === 'transform_request') {
        if (!Object.keys(items).includes('inventory_level_quantities')) {
          items.inventory_level_quantities = {
            resource: 'Incoming Inventory',
          };
        }
        if (item.endTime) {
          const endTime = new Date(item.endTime);
          const currentendTime = new Date(items.inventory_level_quantities.endTime);
          if (!items.inventory_level_quantities.endTime
            || endTime.getTime() > currentendTime.getTime()
          ) {
              items.inventory_level_quantities.endTime = datetimeToString(endTime);
          }
          items.inventory_level_quantities.nextTimeEtl = this.getNextTimeETL(item, 24);
        }
        if (this.store.currentPackage.price > 0) {
          items[item.resource].interval = 'Daily';
        } else {
          items[item.resource].interval = 'Paid plan only';
        }
        items.inventory_level_quantities.limitStartTime = item.limitStartTime;
        items.inventory_level_quantities.startTime = item.startTime;         
      }

      /** footfall_data */
      if (item.resource === 'footfall_data' && item.etlType === 'etl_data_period') {
        if (!Object.keys(items).includes('footfall_data')) {
          items.footfall_data = {
            resource: 'Footfall Data',
          };
        }
        if (this.store.currentPackage.price > 0) {
          items[item.resource].nextTimeEtl = this.getNextTimeETL(item, 24);
        } else {
          items[item.resource].nextTimeEtl = nextTimeEtlMessage;
        }
        items.footfall_data.endTime = item.endTime;
        items[item.resource].interval = 'Hourly';
      }

      /** daily ETL data */
      if (dailyResources.includes(item.resource)) {
        if (item.etlType === 'etl_data_period') {
          if (!Object.keys(items).includes(item.resource)) {
            items[item.resource] = {
              resource: toCamelCase(item.resource),
            };
          }
          if (this.store.currentPackage.price > 0) {
            items[item.resource].interval = 'Daily';
          } else {
            items[item.resource].interval = 'Paid plan only';
          }
          if (item.endTime) {
            items[item.resource].endTime = datetimeToString(
              new Date(item.endTime),
            );
          }
          if (this.store.currentPackage.price > 0) {
            items[item.resource].nextTimeEtl = this.getNextTimeETL(item, 24);
          } else {
            items[item.resource].nextTimeEtl = nextTimeEtlMessage;
          }
        }
      }

      if (item.resource === 'customers' && items[item.resource]) {
        items[item.resource]['count'] = `${this.store.customerCount.modelCount} / ${this.store.customerCount.apiCount}`;              
      }
      if (item.resource === 'locations' && items[item.resource]) {
        items[item.resource]['count'] = `${this.store.locationCount.modelCount} / ${this.store.locationCount.apiCount}`;        
      }
      if (item.resource === 'custom_collections' && items[item.resource]) {
        items[item.resource]['count'] = `${this.store.customCollection.modelCount} / ${this.store.customCollection.apiCount}`;        
      }
      if (item.resource === 'smart_collections' && items[item.resource]) {
        items[item.resource]['count'] = `${this.store.smartCollection.modelCount} / ${this.store.smartCollection.apiCount}`;        
      }
      if (item.resource === 'collections' && items[item.resource]) {
        items[item.resource]['count'] = `${this.store.collectionCount.modelCount} / ${this.store.collectionCount.apiCount}`;
      }
      if (items[item.resource]) {
        items[item.resource].limitStartTime = item.limitStartTime;
        items[item.resource].startTime = item.startTime;
        items[item.resource].lastUpdatedAt = item.lastUpdatedAt;        
      }

    }

    for (const key of Object.keys(items)) {
      if (items[key].endTime) {
        items[key].isUpToDate = this.checkUpToDateData(items[key]);
        if (!items[key].count) {
          items[key].count = '-';
        }
        items[key].resource_key = forceSyncResourceKey(key);
        this.items.push(items[key]);
      }
    }
  }

  public checkUpToDateData(item: any) {
    if ( !this.store.isAthenaFinished
        && item.startTime 
        && item.limitStartTime
        && (new Date(item.startTime)) > (new Date(item.limitStartTime)) ) {
      return 0;
    }
    const delayTime = item.interval === 'Hourly' ? 2 : 25;
    if((new Date((new Date(item.lastUpdatedAt)).setMinutes(0,0,0) + delayTime * 3600000)) > (new Date())) {
      return 1;
    }
    return -1;
  }

  public getNextTimeETL(item: any, period: number) {
    let nextTimeEtl = item.nextTimeEtl;
    if (!nextTimeEtl) {
      nextTimeEtl = datetimeToString(new Date((new Date(item.endTime)).setMinutes(0,0,0) + period*3600000));
    }
    if ((new Date(nextTimeEtl)).getTime() < (new Date()).getTime()) {
      if (period < 24) {
        nextTimeEtl = datetimeToString(new Date((new Date().setMinutes(0,0,0)) + period*3600000));
      } else {
        nextTimeEtl = datetimeToString(new Date((new Date().setHours(0,0,0,0)) + (new Date(item.endTime)).getHours()*3600000));
      }
      if (new Date(nextTimeEtl) < new Date()) {
        nextTimeEtl = datetimeToString(new Date((new Date().setHours(0,0,0,0)) + (new Date(item.endTime)).getHours()*3600000 + 24*3600000));
      }
    }
    return datetimeToString(new Date(nextTimeEtl));
  }

  public async loadETLImportedCount() {
    this.loadingImportCount = true;
    await this.store.loadEtlStatus();
    this.loadingImportCount = false;
    this.renderETLData();
  }

  public async forceSyncResource(item: any) {
    this.syncResource = item.resource;
    this.resourceUpToDate = item.isUpToDate > 0 ? true : false;
    this.triggerDataDialog = true;
    item.isUpToDate = 0;
    const res = await this.store.forceSyncResource(item.resource_key);
    EventBus.$emit(
      'show-snackbar',
      `The ${item.resource} data is syncing right now. This process can take a few minutes.`,
    );
  }

  public async refreshPage() {
    this.items = [];
    this.loading = true;
    await this.store.getStoreData();
    this.renderETLData();
    this.loading = false;
    this.loadETLImportedCount();
    await this.loadActiveJob();
    await this.loadSyncHistory();
  }

  // Clear Deleted Products Methods
  public showClearDeletedDialog() {
    this.clearDeletedDialog = true;
  }

  public async loadActiveJob() {
    try {
      this.activeJob = await productSyncJobs.getActiveJob();

      if (this.isJobActive) {
        this.startJobPolling();
      }
    } catch (error) {
      console.error('Failed to load active job:', error);
      this.activeJob = null;
    }
  }

  public async loadSyncHistory() {
    try {
      const history = await productSyncJobs.getHistory(10);
      // Filter to only show clear_deleted jobs
      this.syncHistory = history.filter(job => job.jobType === 'clear_deleted');
    } catch (error) {
      console.error('Failed to load sync history:', error);
      this.syncHistory = [];
    }
  }

  public startJobPolling() {
    if (this.jobPollingInterval) return;

    this.jobPollingInterval = window.setInterval(async () => {
      // Reentrancy guard - prevent overlapping requests
      if (this.isPolling) return;
      if (!this.activeJob) {
        this.stopJobPolling();
        return;
      }

      this.isPolling = true;
      try {
        this.activeJob = await productSyncJobs.getStatus(this.activeJob.id);

        // Stop polling if job is no longer active
        if (!this.isJobActive) {
          this.stopJobPolling();
          await this.loadSyncHistory();

          // Show completion message
          if (this.activeJob && this.activeJob.status === 'completed') {
            EventBus.$emit('show-snackbar', 'Clear deleted products completed successfully!');
          } else if (this.activeJob && this.activeJob.status === 'failed') {
            EventBus.$emit('show-snackbar', 'Clear deleted products failed. Please try again.');
          }

          // Refresh ETL data
          await this.refreshPage();
        }
      } catch (error) {
        console.error('Failed to poll job status:', error);
      } finally {
        this.isPolling = false;
      }
    }, 10000); // Poll every 10 seconds
  }

  public stopJobPolling() {
    if (this.jobPollingInterval) {
      window.clearInterval(this.jobPollingInterval);
      this.jobPollingInterval = null;
      this.isPolling = false;
    }
  }

  public async initiateClearDeleted() {
    this.initiatingClear = true;
    try {
      await productSyncJobs.initiateClearDeleted();
      this.clearDeletedDialog = false;

      // Load the new active job
      await this.loadActiveJob();

      EventBus.$emit(
        'show-snackbar',
        'Clear deleted products process started. This may take 15-30 minutes.',
      );
    } catch (error: any) {
      const message = error.response?.data?.message || error.message || 'Failed to start process';
      EventBus.$emit('show-snackbar', message);
    } finally {
      this.initiatingClear = false;
    }
  }

  public async cancelActiveJob() {
    if (!this.activeJob) return;

    this.cancellingJob = true;
    try {
      await productSyncJobs.cancelJob(this.activeJob.id);
      await this.loadActiveJob();
      await this.loadSyncHistory();
      EventBus.$emit('show-snackbar', 'Job cancelled successfully.');
    } catch (error: any) {
      const message = error.response?.data?.message || error.message || 'Failed to cancel job';
      EventBus.$emit('show-snackbar', message);
    } finally {
      this.cancellingJob = false;
    }
  }
}
